import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { DIALOG_MSG_DO_YOU_WISH_CONTINUE, PromptConfirmDialogComponent } from "@merri/common";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CommonWorkflowStep, LoggingService, SnackBarService } from "src/app/core";
import { ShowroomWorkflow, ShowroomWorkflowSource, ShowroomWorkflowStep } from "../../../models";
import { ShowroomShowInquiryService } from "../../../services/showroom-show-inquiry.service";
import { ShowroomShortlistItem } from "../../models/showroom-shortlist-item";
import { ShowroomShortlistOverlayInput } from "../../models/showroom-shortlist-overlay-input";
import { ShowroomShortlistOverlayRef } from "../../models/showroom-shortlist-overlay-ref";
import { COMPONENT_OVERLAY_REF, ShowroomShortlistService } from "../../services/showroom-shortlist.service";
import { ShowroomShortlistOverlayItemDialogComponent } from "../showroom-shortlist-overlay-item-dialog/showroom-shortlist-overlay-item-dialog.component";

const Workflow = ShowroomWorkflow.Shortlist;
const WorkflowSource = ShowroomWorkflowSource.ShowroomShortlistOverlay;

@Component({
    selector: "app-showroom-shortlist-overlay",
    templateUrl: "./showroom-shortlist-overlay.component.html",
    styleUrls: ["./showroom-shortlist-overlay.component.scss"],
    host: { class: "mat-elevation-z3" },
    standalone: false
})
export class ShowroomShortlistOverlayComponent implements OnInit, OnDestroy {
  private _childPromptOpen: boolean;
  private _destroyed$: Subject<void> = new Subject();

  loading: boolean;
  error: string;
  shortlistItems: ShowroomShortlistItem[];

  constructor(
    @Inject(COMPONENT_OVERLAY_REF) private _showroomShortlistOverlayRef: ShowroomShortlistOverlayRef,
    @Inject(MAT_DIALOG_DATA) public data: ShowroomShortlistOverlayInput,
    private _showroomShortlistService: ShowroomShortlistService,
    private _showroomShowInquiryService: ShowroomShowInquiryService,
    private _matDialog: MatDialog,
    private _loggingService: LoggingService,
    private _snackBarService: SnackBarService,
  ) {}

  ngOnInit(): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist overlay component init",
      "ShowroomShortlistOverlayComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoad,
      WorkflowSource,
    );

    this._initData(this.data.variantSkus);

    this._showroomShortlistOverlayRef.overlay
      .backdropClick()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          if (this._childPromptOpen) {
            return;
          }

          this._close(CommonWorkflowStep.ModalCloseBackdrop);
        },
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onInquireNowClick(): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist inquire now click",
      "ShowroomShortlistOverlayComponent.onInquireNowClick",
      Workflow,
      ShowroomWorkflowStep.ShowroomInquireNowClick,
      WorkflowSource,
    );

    this._showroomShowInquiryService.openShowroomInquiry("Shortlist");
    this._close(ShowroomWorkflowStep.ShowroomsShortlistModalCloseInquireNowClick);
  }

  onDeleteClick(item: ShowroomShortlistItem, index: number): void {
    this._childPromptOpen = true;

    this._loggingService.logWorkflow(
      "Showroom shortlist delete item attempt",
      "ShowroomShortlistOverlayComponent.onDeleteClick",
      Workflow,
      ShowroomWorkflowStep.ShowroomsShortlistOverlayDeleteItemAttempt,
      WorkflowSource,
      {
        DeletedVariantSkuId: `${item.variantSkuId}`,
      },
    );

    PromptConfirmDialogComponent.promptYesConfirm$(this._matDialog, {
      header: DIALOG_MSG_DO_YOU_WISH_CONTINUE,
      message: `Are you sure you want to delete this item?<br><b>${item.skuName}</b>`,
      title: "Remove Item from Wish List",
      yesColor: "base",
    }).subscribe({
      next: result => {
        if (result) {
          this._showroomShortlistService.removeItemsFromShortlist(this.data.eventId, [this.shortlistItems[index].variantSkuId], "Wishlist");
          this.shortlistItems.splice(index, 1);
          this._snackBarService.show({
            message: "Item removed from Wish List",
            duration: 2500,
            action: "Hide",
          });

          this._loggingService.logWorkflow(
            "Showroom shortlist delete item yes",
            "ShowroomShortlistOverlayComponent.onDeleteClick",
            Workflow,
            ShowroomWorkflowStep.ShowroomsShortlistOverlayDeleteItemYes,
            WorkflowSource,
            {
              DeletedVariantSkuId: `${item.variantSkuId}`,
            },
          );
        } else {
          this._loggingService.logWorkflow(
            "Showroom shortlist delete item no",
            "ShowroomShortlistOverlayComponent.onDeleteClick",
            Workflow,
            ShowroomWorkflowStep.ShowroomsShortlistOverlayDeleteItemNo,
            WorkflowSource,
            {
              DeletedVariantSkuId: `${item.variantSkuId}`,
            },
          );
        }

        this._childPromptOpen = false;
      },
    });
  }

  onShortlistItemZoomClick(item: ShowroomShortlistItem): void {
    this._childPromptOpen = true;
    this._matDialog
      .open<ShowroomShortlistOverlayItemDialogComponent, ShowroomShortlistItem>(ShowroomShortlistOverlayItemDialogComponent, {
        autoFocus: false,
        restoreFocus: false,
        data: item,
      })
      .afterClosed()
      .pipe()
      .subscribe({
        next: () => {
          this._childPromptOpen = false;
        },
      });
  }

  private _initData(variantSkus: number[]): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist overlay component data attempt",
      "ShowroomShortlistOverlayComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoadDataAttempt,
      WorkflowSource,
    );

    if (variantSkus.length) {
      this.loading = true;
      this._showroomShortlistService
        .getShortlistItems$(variantSkus)
        .pipe(takeUntil(this._destroyed$))
        .subscribe({
          next: items => {
            this.shortlistItems = this._getSortedShortlistItems(items);

            this._loggingService.logWorkflow(
              "Showroom shortlist overlay component data success",
              "ShowroomShortlistOverlayComponent.ngOnInit",
              Workflow,
              CommonWorkflowStep.ComponentLoadDataSuccess,
              WorkflowSource,
              {
                ShortlistItemCount: `${this.shortlistItems.length}`,
              },
            );

            this.loading = false;
          },
        });
    } else {
      this.shortlistItems = [];
    }
  }

  private _getSortedShortlistItems(items: ShowroomShortlistItem[]): ShowroomShortlistItem[] {
    return items.sort((a, b) => {
      const categoryResult = a.category.localeCompare(b.category);
      if (categoryResult === 0) {
        const subCategoryResult = a.subCategory.localeCompare(b.subCategory);

        if (subCategoryResult === 0) {
          return a.skuName.localeCompare(b.skuName);
        }

        return subCategoryResult;
      }

      return categoryResult;
    });
  }

  private _close(workflowStep: string): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist overlay close",
      "ShowroomShortlistOverlayComponent._close",
      Workflow,
      workflowStep,
      WorkflowSource,
    );

    this._showroomShortlistOverlayRef.close();
  }
}
